import { useContext, useEffect, useMemo } from 'react';

import {
  ColSize,
  ContentSection,
  DatePickerForm,
  Form,
  InputForm,
  RowSize,
  SelectForm,
} from '../../../../../../../shared/components';

import { GenericDataFormContext, QuotationInfoContext, StepOneFormContext } from '../../contexts';

import { ValidateForm } from '../../../../../../../shared/utils/validateForm';
import { OPTION_YES_OR_NOT } from '../../../../../../../shared/interfaces';
import dayjs from 'dayjs';
import { isValidCodeRenew } from '../../../utils';
import { checkValue } from '../../../../../../../shared/utils';
import { TypeOfPerson } from '../../../utils/type-person.enum';
import { QuotationGroups } from '../../../utils/quotation-groups.enum';

export const ClientDataSection = () => {
  const { clientSelected, additionalDataClientSelected } = useContext(StepOneFormContext);
  const { quotationInfo } = useContext(QuotationInfoContext);

  const validateForm = new ValidateForm();
  const {
    clientDataSectionForm,
    documentTypeList,
    genderList,
    isLoadingDocumentTypeList,
    isLoadingGenderList,
    isLoadingIvaConditionList,
    isLoadingLocalities,
    isLoadingNationalityList,
    isLoadingProvinceList,
    isLoadingTypePersonList,
    isLoadingTypeSocietyList,
    ivaConditionList,
    nationalityList,
    provinceList,
    typePersonList,
    typeSocietyList,
    getLocalitiesByProvinceFromState,
  } = useContext(GenericDataFormContext);

  const localities = useMemo(
    () => getLocalitiesByProvinceFromState(clientSelected?.codProvincia!) ?? [],
    [getLocalitiesByProvinceFromState, clientSelected]
  );

  const isJuridicalPerson = useMemo(
    () => clientSelected?.tipoPersona === TypeOfPerson.JURIDICA,
    [clientSelected]
  );

  const isConsortiumPerson = useMemo(
    () => clientSelected?.tipoPersona === TypeOfPerson.CONSORCIO,
    [clientSelected]
  );

  const isVidaObligatoriaNomina = useMemo(
    () => quotationInfo?.groupSelected === QuotationGroups.VIDA_OBLIGATORIA,
    [quotationInfo?.groupSelected]
  );

  const isAccidentesPersonales = useMemo(
    () => quotationInfo?.groupSelected === QuotationGroups.AP,
    [quotationInfo?.groupSelected]
  );

  const setValuesForm = () => {
    const selectedDocumentType = clientSelected?.tipoDocumento;
    clientDataSectionForm.setFieldValue('documentType', selectedDocumentType);

    const selectedDocumentNumber = clientSelected?.nroDocumento;
    clientDataSectionForm.setFieldValue(
      'documentNumber',
      selectedDocumentNumber
    );

    const selectedCuit = clientSelected?.cuit;
    clientDataSectionForm.setFieldValue('cuit', selectedCuit);

    const selectedFullName = clientSelected?.nombre;
    clientDataSectionForm.setFieldValue('fullName', selectedFullName);

    const selectedProvince = clientSelected?.codProvincia;
    clientDataSectionForm.setFieldValue('province', selectedProvince);

    const selectedLocation = `${clientSelected?.sufijoCodigoPostal}${clientSelected?.codigoPostal}`;
    clientDataSectionForm.setFieldValue('location', selectedLocation);

    const selectedIva = clientSelected?.condicionIVA;
    clientDataSectionForm.setFieldValue('ivaCondition', selectedIva);

    const selectedPersonType = clientSelected?.tipoPersona;
    clientDataSectionForm.setFieldValue('personType', selectedPersonType);

    const selectedAddres = additionalDataClientSelected?.domicilio;
    if (checkValue(selectedAddres)) {
      clientDataSectionForm.setFieldValue('address', selectedAddres);
    };
    const selectedNationalityCode = additionalDataClientSelected?.codigoNacionalidad;
    if (checkValue(selectedNationalityCode)) {
      clientDataSectionForm.setFieldValue('nationality', selectedNationalityCode);
    };
    const selectedSexCode = additionalDataClientSelected?.codigoSexo;
    if (checkValue(selectedSexCode)) {
      clientDataSectionForm.setFieldValue('genderType', selectedSexCode);
    };
    const selectedCompanyTypeCode = additionalDataClientSelected?.codigoTipoSociedad;
    if (checkValue(selectedCompanyTypeCode)) {
      clientDataSectionForm.setFieldValue('societyType', selectedCompanyTypeCode);
    };
    const selectedInscriptionDate = additionalDataClientSelected?.fechaInscripcion;
    if (checkValue(selectedInscriptionDate)) {
      clientDataSectionForm.setFieldValue('inscriptionDate', dayjs(selectedInscriptionDate, 'DD/MM/YYYY'));
    };
    const selectedContractDate = additionalDataClientSelected?.fechaContrato;
    if (checkValue(selectedContractDate)) {
      clientDataSectionForm.setFieldValue('contractDate', dayjs(selectedContractDate, 'DD/MM/YYYY'));
    };
    const selectedInscriptionNumber = additionalDataClientSelected?.numeroInscripcion;
    if (checkValue(selectedInscriptionNumber)) {
      clientDataSectionForm.setFieldValue('inscriptionNumber', selectedInscriptionNumber);
    };
    const selectedPerceptionAgent = additionalDataClientSelected?.agentePercepcion;
    if (checkValue(selectedPerceptionAgent)) {
      clientDataSectionForm.setFieldValue('perceptionAgent', selectedPerceptionAgent);
    };
  };

    useEffect(() => {
      setValuesForm();
      /* eslint-disable-next-line */
    }, []);

    return (
      <Form
        formProps={{
          form: clientDataSectionForm,
          name: 'client-data-form',
        }}
      >
        <ContentSection level='h3' title='Datos del cliente'>
          <RowSize>
            <ColSize size={25}>
              <SelectForm
                formProps={{
                  label: 'Tipo de Documento:',
                  name: 'documentType',
                  rules: isJuridicalPerson
                    ? []
                    : validateForm.generateRules({
                      label: 'tipo de documento',
                      rules: ['required'],
                    }),
                }}
                selectProps={{
                  disabled: !(clientSelected && clientSelected.nroAsegurado === "0"),
                  loading: isLoadingDocumentTypeList,
                  options: documentTypeList,
                }}
              />
            </ColSize>

            <ColSize size={25}>
              <InputForm
                formProps={{
                  label: 'Número de Documento:',
                  name: 'documentNumber',
                  rules: validateForm.generateRules({
                    label: 'número de documento',
                    rules: ['required', 'number'],
                  }),
                }}
                inputProps={{
                  disabled: !(clientSelected && clientSelected.nroAsegurado === "0"),
                  placeholder: 'Número de documento',
                  maxLength: 8 
                }}
              />
            </ColSize>

            <ColSize size={25}>
              <InputForm
                formProps={{
                  label: 'CUIT:',
                  name: 'cuit',
                  rules: validateForm.generateRules({
                    label: 'CUIT (Solo números, sin guiones)',
                    rules: ['number'],
                  }),
                }}
                inputProps={{
                  disabled: !(clientSelected && clientSelected.nroAsegurado === "0"),
                  placeholder: 'Número de cuit (Solo números, sin guiones)',
                }}
              />
            </ColSize>

            <ColSize size={25}>
              <InputForm
                formProps={{
                  label: 'Apellido y Nombre:',
                  name: 'fullName',
                  rules: validateForm.generateRules({
                    label: 'apellido y nombre',
                    rules: ['required'],
                  }),
                }}
                inputProps={{
                  disabled: !(clientSelected && clientSelected.nroAsegurado === "0"),
                  placeholder: 'Apellido y nombre',
                }}
              />
            </ColSize>

            <ColSize size={25}>
              <SelectForm
                formProps={{
                  label: 'Género',
                  name: 'genderType',
                  rules: isJuridicalPerson || isConsortiumPerson
                    ? []
                    : validateForm.generateRules({
                      label: 'género',
                      rules: ['required'],
                    }),
                }}
                selectProps={{
                  disabled: genderList.length < 1 || (additionalDataClientSelected?.codigoSexo !== "0" 
                    &&  additionalDataClientSelected?.codigoSexo !== "" 
                    &&  additionalDataClientSelected?.codigoSexo !== undefined
                    && additionalDataClientSelected?.codigoSexo !== "undefined"),
                  loading: isLoadingGenderList,
                  options: genderList,
                }}
              />
            </ColSize>

            <ColSize size={25}>
              <SelectForm
                formProps={{
                  label: 'Nacionalidad',
                  name: 'nationality',
                  rules: isJuridicalPerson || isConsortiumPerson || isVidaObligatoriaNomina || isAccidentesPersonales
                    ? []
                    : validateForm.generateRules({
                      label: 'nacionalidad',
                      rules: ['required'],
                    }),
                }}
                selectProps={{
                  showSearch: true,
                  disabled: nationalityList.length < 1 || (additionalDataClientSelected?.codigoNacionalidad !== "0"
                     &&  additionalDataClientSelected?.codigoNacionalidad !== null 
                     &&  additionalDataClientSelected?.codigoSexo !== undefined
                     &&  additionalDataClientSelected?.codigoNacionalidad !== "undefined"),
                  loading: isLoadingNationalityList,
                  options: nationalityList,
                }}
              />
            </ColSize>

            <ColSize size={25}>
              <SelectForm
                formProps={{
                  label: 'Provincia:',
                  name: 'province',
                  rules: validateForm.generateRules({
                    label: 'provincia',
                    rules: ['required'],
                  }),
                }}
                selectProps={{
                  disabled: nationalityList.length < 1 || (clientSelected?.codProvincia !== "0"
                     &&  clientSelected?.codProvincia !== null 
                     &&  clientSelected?.codProvincia !== undefined
                     &&  clientSelected?.codProvincia !== "undefined"),
                  loading: isLoadingProvinceList,
                  options: provinceList,
                }}
              />
            </ColSize>

            <ColSize size={25}>
              <SelectForm
                formProps={{
                  label: 'Localidad:',
                  name: 'location',
                  rules: validateForm.generateRules({
                    label: 'localidad',
                    rules: ['required'],
                  }),
                }}
                selectProps={{
                  disabled: nationalityList.length < 1 || (clientSelected?.codigoPostal !== "0"
                     &&  clientSelected?.codigoPostal !== null 
                     &&  clientSelected?.codigoPostal !== undefined
                     &&  clientSelected?.codigoPostal !== "undefined"),
                  loading: isLoadingLocalities,
                  options: localities,
                }}
              />
            </ColSize>

            <ColSize size={25}>
              <InputForm
                formProps={{
                  label: 'Domicilio:',
                  name: 'address',
                  rules: validateForm.generateRules({
                    label: 'domicilio',
                    rules: ['required'],
                  }),
                }}
                inputProps={{ placeholder: 'Senillosa 342 1B', disabled: additionalDataClientSelected?.domicilio !== "0" 
                  &&  additionalDataClientSelected?.domicilio !== ""
                  &&  additionalDataClientSelected?.domicilio !== undefined
                  &&  additionalDataClientSelected?.domicilio !== "undefined"}}
              />
            </ColSize>

            <ColSize size={25}>
              <SelectForm
                formProps={{
                  label: 'IVA:',
                  name: 'ivaCondition',
                  rules: validateForm.generateRules({
                    label: 'iva',
                    rules: ['required'],
                  }),
                }}
                selectProps={{
                  disabled: true,
                  loading: isLoadingIvaConditionList,
                  options: ivaConditionList,
                }}
              />
            </ColSize>

            <ColSize size={25}>
              <SelectForm
                formProps={{
                  label: 'Agente de Percepción:',
                  name: 'perceptionAgent',
                }}
                selectProps={{
                  loading: !OPTION_YES_OR_NOT,
                  options: OPTION_YES_OR_NOT,
                  disabled: isValidCodeRenew(additionalDataClientSelected?.agentePercepcion)
                }}
              />
            </ColSize>

            <ColSize size={25}>
              <SelectForm
                formProps={{
                  label: 'Tipo de Persona:',
                  name: 'personType',
                  rules: validateForm.generateRules({
                    label: 'persona',
                    rules: ['required'],
                  }),
                }}
                selectProps={{
                   disabled: typeSocietyList.length < 1 || (additionalDataClientSelected?.codigoTipoSociedad !== "0" && additionalDataClientSelected?.codigoTipoSociedad !== "undefined"),
                  loading: isLoadingTypePersonList,
                  options: typePersonList,
                }}
              />
            </ColSize>

            <ColSize size={25}>
              <SelectForm
                formProps={{
                  label: 'Tipo de Sociedad:',
                  name: 'societyType',
                  rules: validateForm.generateRules({
                    label: 'sociedad',
                    rules: ['required'],
                  }),
                }}
                selectProps={{
                  disabled: typeSocietyList.length < 1 || (additionalDataClientSelected?.codigoTipoSociedad !== "0" && additionalDataClientSelected?.codigoTipoSociedad !== "undefined" && additionalDataClientSelected?.codigoTipoSociedad !== undefined),
                  loading: isLoadingTypeSocietyList,
                  options: typeSocietyList,
                  showSearch: true,
                }}
              />
            </ColSize>

            <ColSize size={25}>
              <DatePickerForm
                formProps={{
                  label: 'Fecha de Inscripción:',
                  name: 'inscriptionDate',
                }}
              />
            </ColSize>

            <ColSize size={25}>
              <InputForm
                formProps={{
                  label: 'Nro. de Inscripción:',
                  name: 'inscriptionNumber',
                  rules: validateForm.generateRules({
                    label: 'nro. de inscripción',
                    rules: ['number'],
                  }),
                }}
              />
            </ColSize>

            <ColSize size={25}>
              <DatePickerForm
                formProps={{
                  label: 'Fecha de Contrato:',
                  name: 'contractDate',
                }}
              />
            </ColSize>
          </RowSize>
        </ContentSection>
      </Form>
    );
  };
